import * as angular from 'angular';
import * as $ from 'jquery';
import { Store } from 'redux';
import { merge as observableMerge, Observable, BehaviorSubject } from 'rxjs';
import { first, withLatestFrom, map, distinctUntilChanged, shareReplay } from 'rxjs/operators';

import { ofType } from '@mode/legacy/util-domain';
import { Actions } from '~/actions';
import { SelectedReportSelectors } from '~/selectors/selected-report.selectors';
import { NotebookDataService } from '~/services/notebook-data-service';
import { AppStore } from '~/types/main.types';

import { ChartFacade } from '@mode/charts/contract';
import { ChartActions, TempActions, fromChartSelectors } from '@mode/charts/domain';
import { QueryResource } from '@mode/legacy/data-access-model-js';
import { ReportEditorFacade, fromReportEditorSelectors, reportEditActions } from '@mode/report-editor/domain';
import { ChartTypes } from '@mode/shared/contract-common';
import { safeParseHTML } from '@mode/shared/util-js';
import { QueryActionType } from '~/actions/query.actions';
import { routerSelector } from '~/reducers/selectors';
import { ChartModelActionType } from '../actions/chart.actions';
import * as actions from '../actions/editor-actions';
import { EditorActionType } from '../actions/editor-actions';
import { ReportLayoutActions } from '../actions/report-layout.actions';
import { WorkspaceActionType } from '../actions/workspace.actions';
import { SchemaTabs, VizTypes } from '../common/types';
import { EditorState } from '../reducers/editor-reducers';
import { ModeApp } from '../reducers/index';
import { ReportLayoutSelectors } from '../selectors/report-layout.selectors';
import { ReportSaveService } from './report-save-service';
import { WindowMessageService } from './window-message-service';

import { MetricsActions } from '@mode/metrics/domain';
import EditorService from './editor-service';
import { DEFAULT_RESIZABLE_STATE, ResizableState } from '~/types/resizable-state.types';

export default class ViewService {
  static $inject = [
    '$rootScope',
    '$state',
    '$timeout',
    '$window',
    'StoreService',
    'RxStoreService',
    'EditorService',
    'actions$',
    'ChartFacade',
    'ReportEditorFacade',
    'WindowMessageService',
    'ReportSaveService',
    'NotebookDataService',
    'PythonVisualizationModel',
  ];
  views: EditorState;

  useRefactoredRouting: boolean;
  // Store tokens in layout as workaround until browser component is refactored into smaller components
  chartsInLayout: Set<string>;
  tablesInLayout: Set<string>;

  // Single state for resizable schema browser
  private resizableState: BehaviorSubject<ResizableState> = new BehaviorSubject(DEFAULT_RESIZABLE_STATE);

  get schemaBrowserWidth$() {
    return this.resizableState.asObservable().pipe(
      map((resizableState) => resizableState.schemaBrowserWidth),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  get schemaBrowserWidthValue() {
    return this.resizableState.getValue().schemaBrowserWidth;
  }

  updateSchemaBrowserWidth(width: number) {
    const state = this.resizableState.getValue();
    this.resizableState.next({ ...state, schemaBrowserWidth: width });
  }

  get editorBrowserWidth$() {
    return this.resizableState.asObservable().pipe(
      map((resizableState) => resizableState.editorBrowserWidth),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  get editorBrowserWidthValue() {
    return this.resizableState.getValue().editorBrowserWidth;
  }

  updateEditorBrowserWidth(width: number) {
    const state = this.resizableState.getValue();
    this.resizableState.next({ ...state, editorBrowserWidth: width });
  }

  get isResizingSchemaBrowser$() {
    return this.resizableState.asObservable().pipe(
      map((resizableState) => resizableState.isResizingSchemaBrowser),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  setIsResizingSchemaBrowser(isResizing: boolean) {
    const state = this.resizableState.getValue();
    this.resizableState.next({ ...state, isResizingSchemaBrowser: isResizing });
  }

  get isResizingEditorBrowser$() {
    return this.resizableState.asObservable().pipe(
      map((resizableState) => resizableState.isResizingEditorBrowser),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  setIsResizingEditorBrowser(isResizing: boolean) {
    const state = this.resizableState.getValue();
    this.resizableState.next({ ...state, isResizingEditorBrowser: isResizing });
  }

  get isResizing$() {
    return this.resizableState.asObservable().pipe(
      map((resizableState) => resizableState.isResizingSchemaBrowser || resizableState.isResizingEditorBrowser),
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  constructor(
    private $rootScope: angular.IRootScopeService,
    private $state,
    private $timeout: angular.ITimeoutService,
    private $window: angular.IWindowService,
    private store: Store<ModeApp>,
    private rxStore: AppStore,
    private editorService: EditorService,
    private actions$: Observable<Actions>,
    private chartFacade: ChartFacade,
    private editorFacade: ReportEditorFacade,
    private wms: WindowMessageService,
    private rss: ReportSaveService,
    private notebookDataService: NotebookDataService,
    private PythonVisualizationModel
  ) {
    this.views = store.getState().editor;
    rxStore
      .select(ReportLayoutSelectors.chartTokensInLayout)
      .subscribe((tokens) => (this.chartsInLayout = new Set(tokens)));
    rxStore
      .select(ReportLayoutSelectors.tableTokensInLayout)
      .subscribe((tokens) => (this.tablesInLayout = new Set(tokens)));

    wms.subscribe(this, (msg) => {
      switch (msg.type) {
        case WindowMessageService.MSG_TYPES.SelectTableColumn:
          this.selectTableColumn(msg.data.id, msg.data.column);
          break;
      }

      this.$rootScope.$evalAsync();
    });

    store.subscribe(() => {
      this.updateEditorState(store.getState().editor);
    });

    // TODO: further simplify selecting chart after creating it
    this.actions$.pipe(ofType(ChartActions.createChartSuccess)).subscribe((action) => {
      const { queryToken, focus } = action;
      this.selectV2Chart({ token: action.payload.token, queryToken }, focus);
      this.$rootScope.$broadcast('editor.createChart', { vizToken: action.payload.token });
    });

    // TODO refactor out these when report editor routing is seperate from workspace.
    this.actions$.pipe(ofType(ChartActions.destroyChartSuccess)).subscribe((action) => {
      this.$rootScope.$broadcast('editor.destroyChart', { vizToken: action.payload });
    });

    this.actions$.pipe(ofType(QueryActionType.CreateQuerySuccess)).subscribe((action) => {
      this.$rootScope.$broadcast('editor.createQuery', { resource: action.payload });
    });

    this.actions$.pipe(ofType(MetricsActions.createMetricQuerySuccess)).subscribe((action) => {
      this.$rootScope.$broadcast('editor.createMetricQuery', { resource: action.query });
    });

    this.actions$.pipe(ofType(TempActions.Type.DestroyQuerySuccess)).subscribe((action) => {
      this.editorService.destroySession(action.token);
      this.$rootScope.$broadcast('editor.destroyQuery');
    });

    // Redux select chart
    this.actions$
      .pipe(
        ofType(ChartActions.selectChart),
        withLatestFrom(
          this.rxStore.select('client', 'selectedReport', 'queries'),
          this.rxStore.select(fromReportEditorSelectors.selectSelectedQueryToken),
          this.rxStore.select(fromChartSelectors.selectQueryTokens),
          this.rxStore.select(fromChartSelectors.selectV1Views),
          this.rxStore.select(fromChartSelectors.selectColorPaletteTokens),
          this.rxStore.select(fromChartSelectors.selectVersions)
        )
      )
      .subscribe(([action, queries, selectedQueryToken, queryTokens, views, colorTokens, versions]) => {
        const queryToken = queryTokens.get(action.token);
        const query = queries.getRecord(queryToken);

        if (query != null) {
          // null check to prevent errors on the report view page
          this.store.dispatch(
            actions.selectChart(
              true,
              {
                view: views.get(action.token),
                color_palette_token: colorTokens.get(action.token),
                token: action.token,
                view_version: versions.get(action.token),
              },
              query
            )
          );

          // Keep the selected query token in sync with the visualization for the top tabs
          if (selectedQueryToken !== queryToken) {
            this.rxStore.dispatch(
              reportEditActions.selectQuery({
                token: queryToken,
              })
            );
          }
        }
      });

    this.actions$
      .pipe(
        ofType(reportEditActions.selectLegacyTable),
        withLatestFrom(
          this.rxStore.select('client', 'selectedReport', 'queries'),
          this.rxStore.select('client', 'selectedReport', 'tables')
        )
      )
      .subscribe(([action, queries, tables]) => {
        const query = queries.getRecord(action.queryToken);
        const table = tables.getRecord(action.token);
        this.store.dispatch(actions.selectTable(true, table.resource, query));
      });

    // TODO: move focusedness into rx state store
    observableMerge(
      this.actions$.pipe(ofType(EditorActionType.RunPreviewTableQuery)),
      this.actions$.pipe(ofType(EditorActionType.InsertQueryText))
    ).subscribe(() => {
      this.editorService.setFocus('sql');
    });
  }

  public emitPreviewUpdate(type: 'Chart' | 'Table' | 'Python', token: string, queryToken: string, view: any) {
    const tempView = angular.copy(view);
    tempView._token = token;
    tempView._dataset = queryToken;

    const data = {};
    const functionType = type === 'Python' ? 'Viz' : type;
    data['update' + functionType] = {
      token: token,
      view: tempView,
      id: `${type}_${token}`.toLowerCase(),
      type: type.toLowerCase(),
    };
    this.$rootScope.$broadcast('preview.update', data);
  }

  updateEditorState(next: EditorState) {
    this.views = next;
  }

  setView(name: string, show: boolean) {
    this.store.dispatch(actions.setView(name, show));
  }

  selectReport(report, query) {
    const DOM = safeParseHTML('<div>' + report.layout + '</div>').body;
    const ids = $(DOM)
      .find('mode-chart,mode-table')
      .toArray()
      .map((chart) => chart.getAttribute('id'))
      .filter((id) => id);

    this.store.dispatch(actions.selectReport(report, ids, query));
    this.rxStore
      .selectNoEmpty(routerSelector)
      .pipe(first())
      .subscribe((route) => {
        // @desc If we're navigating to an edit viz route then chart selection will
        //       result in query selection, and this is at best redundant. Bail on
        //       calling editorFacade.selectQuery in this case
        //
        // 1. subscribe ofType(ChartActions.selectChart)
        // 2. dispatch ChartActions.selectChart
        //  2.1. exec subscribe callback from #1..
        //  2.2. dispatch reportEditActions.selectQuery

        const onImportedDatasetEditorRoute = route.name === 'application.reports.edit.datasets';
        const onVisualizationEditorRoute = route.name === 'application.reports.edit.viz';

        if (
          !onImportedDatasetEditorRoute &&
          !(onVisualizationEditorRoute && typeof route.params.vizToken === 'string')
        ) {
          // @old: duplicate dispatch action for now until redux state is removed and migrated to rxstore
          this.editorFacade.selectQuery(
            // Prefer the route's queryToken over the last-run query token
            // otherwise we end up making an extra `/selects` call for an
            // unrelated querie's results
            route.params.queryToken || query.token
          );
        }
      });
  }

  selectChart(id: string, focus: boolean) {
    const chart = this.rss.getChartById(id);
    if (chart) {
      // SUPER HACKY, but new charts are modified in place without being persisted, and since this is done
      // outside the Rx store, need to delay the select until later.
      this.selectV2Chart(chart, focus);
    } else {
      throw new Error(`chart not found ${id}`);
    }
  }

  selectV2Chart(chart: { token?: string; queryToken: string }, focus: boolean) {
    this.chartFacade.selectChart(chart.token);
  }

  selectTable(id: string, focus: boolean) {
    const table = this.rss.getTableById(id);
    if (table) {
      this.editorFacade.selectLegacyTable(table.token, table.queryToken);
    }
  }

  resetTiledPanesView(): void {
    /*
    The report builder and the editor share the views in the state,
    this leads to changes in the editor effecting the view of the report
    builder and vice versa.  To stop doing that below we reset the views
    to default by minimizing the sql and html views
    */

    this.minimize('sql');
    this.minimize('html');
  }

  selectReportBuilder() {
    this.resetTiledPanesView();
    this.rxStore.dispatch({
      type: WorkspaceActionType.SelectReportBuilder,
    });
  }

  selectTableColumn(id: string, column: string) {
    this.store.dispatch(actions.selectTableColumn(id, column));
  }

  remapQuery(query: QueryResource) {
    this.store.dispatch(actions.remapQuery(query.token, query.mapping_id));
    this.editorService.remapSession(query.mapping_id, query.token);
  }

  selectQuery(query: any, focus: boolean) {
    this.resetTiledPanesView();
    this.store.dispatch(actions.selectQuery(query.token, focus, query));
    this.editorFacade.selectQuery(query.token);
    this.rxStore.dispatch({
      type: WorkspaceActionType.SelectSqlEditor,
    });

    if (focus) {
      this.editorService.setFocus('sql');
    }
  }

  clearSelected() {
    this.store.dispatch(actions.clearSelected());
  }

  toggleSecondarySidebar() {
    this.store.dispatch(actions.toggleSecondarySidebar());
  }

  minimize(name: string, subview?: string) {
    this.store.dispatch(actions.minimize(name, subview));
  }

  maximize(name: string, subview?: string) {
    this.store.dispatch(actions.maximize(name, subview));
  }

  queryRunComplete(queryToken: string) {
    this.store.dispatch(actions.queryRunComplete(queryToken));
  }

  toggleQueryMode(query, $event?) {
    const current = this.store.getState().editor;

    if (!query && $event && $event.altKey) {
      // action to set the builder mode without triggering a refresh
      this.store.dispatch(actions.setBuilderMode(true));
    } else {
      this.store.dispatch(actions.setQueryMode(query));
    }

    if (current.queryMode !== query) {
      this.$timeout(
        () => {
          angular.element(this.$window).trigger('resize');
        },
        0,
        false
      );
    }
  }

  toggleNotebookShown(shown: boolean) {
    const current = this.store.getState().editor;
    // DEPRECATED :: This updates the redux store
    this.store.dispatch(actions.setNotebookShown(shown));

    if (shown) {
      this.notebookDataService.initNotebook();
    }

    if (current.notebookShown !== shown) {
      this.$timeout(
        () => {
          angular.element(this.$window).trigger('resize');
        },
        0,
        false
      );
    }
  }

  /**
   * Routes to the notebook. Do not call this from any link that has a sref
   * to the notebook state.
   */
  goToNotebook() {
    this.$state.go('application.reports.edit.notebook');
  }

  hasChartInLayout(token: string) {
    return this.chartsInLayout.has(token);
  }

  hasTableInLayout(token: string) {
    return this.tablesInLayout.has(token);
  }

  toggleVerticalMode() {
    this.store.dispatch(actions.toggleVerticalMode());
  }

  selectEditorSchemaTab(tab: SchemaTabs) {
    this.store.dispatch(actions.SelectSchemaTab(tab));
  }

  addChartToLayout(token: string) {
    this.rxStore.dispatchThunk(
      fromChartSelectors.createV1orV2ChartTypeSelector(token),
      (chartType) => new ReportLayoutActions.AddViz(token, chartType, VizTypes.Chart)
    );
  }

  addDisplayTableToLayout(token: string) {
    this.rxStore.dispatch(new ReportLayoutActions.AddViz(token, null, VizTypes.Table));
  }

  updateTableExcludedColumn(token: string, remove: boolean, name: string) {
    const table = this.rss.getTableById(token);

    this.rxStore.dispatch({
      type: ChartModelActionType.UpdateTableExcludedColumns,
      columnName: name,
      token,
      remove,
    });
  }

  updateTableColumnSettings(token: string, column: string, columnView: any) {
    const table = this.rss.getTableById(token);
    this.rxStore.dispatch({
      type: ChartModelActionType.UpdateTableColumnSettings,
      columnName: column,
      column: columnView,
      token,
    });
  }

  updateChartType(token: string, type: string, subtype: string, icon: string) {
    const chart = this.rss.getChartById(token);

    if (chart) {
      chart.v1View.selectedChart = type;
      chart.v1View.chartIcon = icon;

      switch (subtype) {
        case 'bar':
          chart.v1View.bar.stacked = false;
          break;
        case 'stackedBar':
          chart.v1View.bar.stacked = true;
          break;
        case 'area':
          chart.v1View.area.expanded = false;
          break;
        case 'expandedArea':
          chart.v1View.area.expanded = true;
          break;
        case 'pie':
          chart.v1View.pie.donut = false;
          break;
        case 'donut':
          chart.v1View.pie.donut = true;
          break;
      }

      this.saveV1ChartSettings(chart, chart.v1View, chart.colorPaletteToken);
    }
  }

  updateChartSettings(token: string, view: ChartTypes.ChartView, colorPaletteToken?: string) {
    const chart = this.rss.getChartById(token);
    if (chart) {
      this.saveV1ChartSettings(chart, view, colorPaletteToken || chart.colorPaletteToken);
    }
  }

  // Copied some logic from chart designer service to avoid circular dependency
  saveV1ChartSettings(chart: ChartTypes.CoreChart, view: ChartTypes.ChartView, colorPaletteToken: string) {
    chart.resource
      .forms('edit')
      .$submit({
        'chart.view': JSON.stringify(view),
        'chart.color_palette_token': colorPaletteToken,
      })
      .then((res) => {
        res._parent = chart.queryToken;
        this.rxStore.dispatch(ChartActions.updateChartSuccess({ resource: res }));
        this.selectChart(chart.token, false);
        this.emitPreviewUpdate('Chart', chart.token, chart.queryToken, res.view);
      });
  }

  updateChartSettingsForType(token: string, viewForType: any) {
    const chart = this.rss.getChartById(token);

    if (chart) {
      const newView = {
        ...chart.v1View,
        [chart.v1View.selectedChart]: viewForType,
      };
      this.updateChartSettings(token, newView);
    }
  }

  updateChartPaletteForBigNumber(chartToken: string, paletteToken: string, paletteColors: string[]) {
    const chart = this.rss.getChartById(chartToken);

    if (chart) {
      const newView = {
        ...chart.v1View,
        bigNumber: {
          ...chart.v1View.bigNumber,
          colors: paletteColors,
        },
      };

      this.updateChartSettings(chartToken, newView, paletteToken);
    }
  }

  updateChartTheme(token: string, theme: string, colors: Array<string>) {
    const chart = this.rss.getChartById(token);
    if (chart) {
      chart.v1View.theme = theme;
      chart.v1View.chartColors = colors;
      this.updateChartSettings(token, chart.v1View);
    }
  }

  updateVizSettings(id: string /* includes type_token */, view: any) {
    const viz = this.rss.getVizById(id);
    if (viz) {
      const type = id.split('_')[0];
      if (type === 'python') {
        // custom python view mangling here because there is no "view"
        viz.title = view.title;
        this.PythonVisualizationModel.update(viz).then((savedPython) => {
          this.emitPreviewUpdate('Python', savedPython.token, undefined, viz.renderEmbed());
        });
      } else if (type === 'chart') {
        this.updateChartSettings(viz.token, view);
      }
    }
  }

  reloadPreviewFrame() {
    this.rxStore.dispatchThunk(
      SelectedReportSelectors.getLayout,
      (layout) => new ReportLayoutActions.ReloadFrame(layout)
    );
  }
}
